import { useEffect, useRef, useState } from 'react';

// Animate a number from 0 → target on mount (and again whenever `target`
// changes). Returns the current animated value rounded to `decimals`.
//
// Respects prefers-reduced-motion: if the user has reduced motion enabled,
// the hook short-circuits to the target value immediately.
export function useCountUp(target, { duration = 700, decimals = 0 } = {}) {
  const safeTarget = Number.isFinite(+target) ? +target : 0;
  const [value, setValue] = useState(safeTarget);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || duration <= 0) {
      setValue(safeTarget);
      return;
    }

    fromRef.current = 0;
    startRef.current = null;

    const tick = (now) => {
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const next = fromRef.current + (safeTarget - fromRef.current) * eased;
      setValue(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [safeTarget, duration]);

  return decimals > 0
    ? Number(value.toFixed(decimals))
    : Math.round(value);
}
